import {FormEvent, useEffect, useState} from 'react';
import {keepPreviousData, useQuery, useQueryClient} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import {errorMessage, jsonInit} from '../api/client';
import ErrorBox from '../components/feedback/ErrorBox';
import Icon from '../components/Icon';
import {useAuth} from '../hooks/useAuth';
import {confirm} from '../hooks/useConfirm';
import {toast} from '../hooks/useToast';
import {formatDate, formatDateTime, relativeDay} from '../lib/dates';
import {AdminUser, InviteOutcome, UsersFilter, UsersPage} from '../types';
import Button from '../components/Button';

// The trial-user list: who asked to join, who was invited and when, whether Cloudflare has
// verified their address (so our emails can reach them), and whether they have actually used
// the app. Admin only (the API checks is_admin in D1 on every call).

const FILTERS: {key: UsersFilter; label: string}[] = [
    {key: 'all', label: 'All'},
    {key: 'asked', label: 'Asked to join'},
    {key: 'invited', label: 'Invited, not in yet'},
    {key: 'active', label: 'Used the app'},
    {key: 'unverified', label: 'Email unverified'},
    {key: 'admins', label: 'Admins'}
];

const statusOf = (u: AdminUser): {label: string; cls: string} => {
    if (!u.isActive) return {label: u.invitedAt ? 'Deactivated' : 'Asked to join', cls: 'bg-kind-plain-bg text-kind-plain-fg'};
    if (u.loginCount === 0) return {label: 'Invited', cls: 'bg-marigold-soft text-marigold-text'};
    return {label: `Active · ${u.loginCount} ${u.loginCount === 1 ? 'login' : 'logins'}`, cls: 'bg-shoot-soft text-shoot-text'};
};

const destinationOf = (u: AdminUser): {label: string; cls: string} => {
    switch (u.cfDestinationStatus) {
        case 'verified':
            return {label: 'Verified', cls: 'bg-shoot-soft text-shoot-text'};
        case 'unverified':
            return {label: 'Awaiting Cloudflare click', cls: 'bg-marigold-soft text-marigold-text'};
        default:
            return {label: 'Not checked', cls: 'bg-kind-plain-bg text-kind-plain-fg'};
    }
};

export default function AdminUsersPage() {
    const {call, claims} = useAuth();
    const queryClient = useQueryClient();

    const [q, setQ] = useState('');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<UsersFilter>('all');
    const [page, setPage] = useState(1);
    const [busy, setBusy] = useState<string | null>(null);

    const [newEmail, setNewEmail] = useState('');
    const [newName, setNewName] = useState('');
    const [inviteNow, setInviteNow] = useState(true);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setSearch(q.trim());
            setPage(1);
        }, 300);
        return () => window.clearTimeout(handle);
    }, [q]);

    const params = new URLSearchParams({q: search, filter, page: String(page), perPage: '25'});
    const users = useQuery({
        queryKey: ['admin-users', search, filter, page],
        queryFn: () => call<UsersPage>(`/api/admin/users?${params}`),
        placeholderData: keepPreviousData
    });

    const run = async (key: string, action: () => Promise<string | void>) => {
        setBusy(key);
        try {
            const message = await action();
            if (message) toast.success(message);
            await queryClient.invalidateQueries({queryKey: ['admin-users']});
        } catch (e) {
            toast.problem(errorMessage(e));
        } finally {
            setBusy(null);
        }
    };

    const inviteOutcome = (outcome: InviteOutcome, verb: string): string => {
        if (outcome.emailSent) return `${verb} ${outcome.user.email}; the invite email is on its way.`;
        if (outcome.destination === 'unverified') {
            return `${verb} ${outcome.user.email}. They still need to click Cloudflare's verification email before an invite can reach them; the invite will go when you tap Invite again after that.`;
        }
        return `${verb} ${outcome.user.email}, but the invite email didn't send. Check the Cloudflare status and try again.`;
    };

    const addUser = (event: FormEvent) => {
        event.preventDefault();
        void run('add', async () => {
            const outcome = await call<InviteOutcome>('/api/admin/users', jsonInit('POST', {email: newEmail.trim(), firstName: newName.trim(), invite: inviteNow}));
            setNewEmail('');
            setNewName('');
            return inviteNow ? inviteOutcome(outcome, 'Added and invited') : `Added ${outcome.user.email} to the list.`;
        });
    };

    const invite = (u: AdminUser) =>
        run(`invite:${u.id}`, async () => inviteOutcome(await call<InviteOutcome>(`/api/admin/users/${u.id}/invite`, jsonInit('POST')), u.inviteEmailSentAt ? 'Re-invited' : 'Invited'));

    const checkDestination = (u: AdminUser, resend: boolean) =>
        run(`cf:${u.id}`, async () => {
            const outcome = await call<InviteOutcome>(`/api/admin/users/${u.id}/check-destination`, jsonInit('POST', {resend}));
            if (resend) {
                return outcome.resent
                    ? `Cloudflare has re-sent its verification email to ${u.email}.`
                    : outcome.destination === 'verified'
                      ? `${u.email} is already verified; nothing to re-send.`
                      : `Cloudflare won't re-send yet: the address was added too recently. Ask them to look in spam for the first one, or try again later.`;
            }
            return `${u.email} is ${outcome.destination === 'verified' ? 'verified' : 'still unverified'} with Cloudflare.`;
        });

    const patch = (u: AdminUser, body: Partial<Pick<AdminUser, 'isAdmin' | 'isActive'>>, message: string) =>
        run(`patch:${u.id}`, async () => {
            await call<AdminUser>(`/api/admin/users/${u.id}`, jsonInit('PATCH', body));
            return message;
        });

    const remove = async (u: AdminUser) => {
        const ok = await confirm({
            title: `Remove ${u.email}?`,
            body: 'They never signed in, so nothing of theirs is lost. They can ask to join again later.',
            confirmLabel: 'Remove',
            destructive: true
        });
        if (!ok) return;
        void run(`remove:${u.id}`, async () => {
            await call<void>(`/api/admin/users/${u.id}`, {method: 'DELETE'});
            return `Removed ${u.email}.`;
        });
    };

    const data = users.data;

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="pt-2">
                <Link to="/more" className="meta mb-1 inline-flex items-center gap-1 no-underline">
                    <Icon name="back" size={16} /> More
                </Link>
                <h1 className="display m-0">People</h1>
                <p className="meta mt-1">
                    {data ? `${data.total} on the list` : 'Loading…'}
                    {data && !data.destinationsApi && ' · Cloudflare checks are off (no API token on the Worker)'}
                </p>
            </header>

            <form onSubmit={addUser} className="card flex flex-col gap-3 p-4">
                <div className="band-label text-ink-2">Add someone</div>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                        className="field sm:flex-1"
                        type="email"
                        placeholder="their@email.com"
                        required
                        value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        disabled={busy === 'add'}
                    />
                    <input
                        className="field sm:w-40"
                        type="text"
                        placeholder="First name"
                        maxLength={60}
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        disabled={busy === 'add'}
                    />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={inviteNow} onChange={e => setInviteNow(e.target.checked)} className="h-5 w-5 accent-shoot" />
                        Send the invite now
                    </label>
                    <Button type="submit" size="sm" busy={busy === 'add'} busyLabel="Adding…" disabled={newEmail.trim() === ''}>
                        {inviteNow ? 'Add and invite' : 'Add to the list'}
                    </Button>
                </div>
                <p className="meta m-0">
                    Adding registers the address with Cloudflare, which emails them a verification link. Our invite can only land once
                    they have clicked it.
                </p>
            </form>

            <div className="flex flex-col gap-3">
                <input
                    className="field"
                    type="search"
                    placeholder="Search by email or name"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    aria-label="Search people"
                />
                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 py-1 [scrollbar-width:none]">
                    {FILTERS.map(f => (
                        <button
                            key={f.key}
                            type="button"
                            className="chip"
                            aria-pressed={filter === f.key}
                            onClick={() => {
                                setFilter(f.key);
                                setPage(1);
                            }}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {users.isError && <ErrorBox error={users.error} onRetry={() => void users.refetch()} />}

            <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {data?.items.map(u => {
                    const status = statusOf(u);
                    const destination = destinationOf(u);
                    const isMe = u.id === claims?.id;
                    const working = busy?.endsWith(u.id) ?? false;
                    return (
                        <li key={u.id} className="card flex flex-col gap-3 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="truncate font-semibold">
                                        {u.firstName ? `${u.firstName} · ` : ''}
                                        {u.email}
                                    </div>
                                    <div className="meta">
                                        Asked {formatDate(u.createdAt)}
                                        {u.invitedAt && ` · invited ${formatDate(u.invitedAt)}${u.invitedBy ? ` by ${u.invitedBy.split('@')[0]}` : ''}`}
                                        {u.inviteEmailSentAt && ` · invite emailed ${relativeDay(u.inviteEmailSentAt)}`}
                                    </div>
                                    <div className="meta">
                                        {u.lastLoginAt ? `Last in ${relativeDay(u.lastLoginAt)} (${formatDateTime(u.lastLoginAt)})` : 'Never signed in'}
                                        {u.lastRequestedAt && ` · last asked for a link ${relativeDay(u.lastRequestedAt)}`}
                                    </div>
                                </div>
                                {u.isAdmin && <span className="pill shrink-0 bg-kind-seed-bg text-kind-seed-fg">Admin</span>}
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <span className={`pill ${status.cls}`}>{status.label}</span>
                                <span className={`pill ${destination.cls}`} title={u.cfDestinationCheckedAt ? `Checked ${formatDateTime(u.cfDestinationCheckedAt)}` : undefined}>
                                    <Icon name={u.cfDestinationStatus === 'verified' ? 'check' : 'reminder'} size={14} strokeWidth={2.2} />
                                    {destination.label}
                                </span>
                                {u.emailConfirmed && <span className="pill bg-kind-plain-bg text-kind-plain-fg">Email proven</span>}
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {!u.isActive && (
                                    <Button size="sm" busy={working} onClick={() => invite(u)}>
                                        {u.invitedAt ? 'Reactivate and invite' : 'Invite'}
                                    </Button>
                                )}
                                {u.isActive && u.loginCount === 0 && (
                                    <Button variant="secondary" size="sm" busy={working} onClick={() => invite(u)}>
                                        {u.inviteEmailSentAt ? 'Resend invite' : 'Send invite email'}
                                    </Button>
                                )}
                                {data.destinationsApi && u.cfDestinationStatus !== 'verified' && (
                                    <>
                                        <Button variant="secondary" size="sm" busy={working} onClick={() => checkDestination(u, false)}>
                                            Check Cloudflare
                                        </Button>
                                        {u.cfDestinationStatus === 'unverified' && (
                                            <Button variant="secondary" size="sm" busy={working} onClick={() => checkDestination(u, true)}>
                                                Resend Cloudflare email
                                            </Button>
                                        )}
                                    </>
                                )}
                                {!isMe && (
                                    <Button variant="secondary" size="sm" busy={working} onClick={() => patch(u, {isAdmin: !u.isAdmin}, `${u.email} is ${u.isAdmin ? 'no longer' : 'now'} an admin.`)}>
                                        {u.isAdmin ? 'Remove admin' : 'Make admin'}
                                    </Button>
                                )}
                                {!isMe && u.isActive && (
                                    <Button variant="secondary" size="sm" danger busy={working} onClick={() => patch(u, {isActive: false}, `${u.email} is deactivated and signed out.`)}>
                                        Deactivate
                                    </Button>
                                )}
                                {!isMe && u.loginCount === 0 && (
                                    <Button variant="secondary" size="sm" danger busy={working} onClick={() => remove(u)}>
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </li>
                    );
                })}
                {data && data.items.length === 0 && (
                    <li className="rounded-[18px] border-[1.5px] border-dashed border-line p-5 text-ink-2">Nobody matches that.</li>
                )}
            </ul>

            {data && data.totalPages > 1 && (
                <nav className="flex items-center justify-between" aria-label="Pages">
                    <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        <Icon name="back" size={18} /> Previous
                    </button>
                    <span className="meta">
                        Page {data.page} of {data.totalPages}
                    </span>
                    <button type="button" className="btn btn-secondary btn-sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                        Next <Icon name="chevron" size={18} />
                    </button>
                </nav>
            )}
        </div>
    );
}
